/**
 * Cross-service and app environment variables with legacy fallbacks.
 * Read cross-service values here (or via Config), not scattered `process.env` in feature code.
 */

function trim(value: string | undefined): string {
  return (value ?? "").trim();
}

function firstNonEmpty(...values: (string | undefined)[]): string {
  for (const v of values) {
    const t = trim(v);
    if (t) return t;
  }
  return "";
}

function stripTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, "");
}

export function getOdooInternalApiBaseUrl(): string {
  return stripTrailingSlashes(
    firstNonEmpty(process.env.ODOO_INTERNAL_API_BASE_URL, process.env.ODOO_SERVER, process.env.ODOO_BASE_URL)
  );
}

export function getOdooInternalApiKey(): string {
  return firstNonEmpty(process.env.ODOO_INTERNAL_API_KEY, process.env.ODOO_BEARER_TOKEN);
}

/** Bearer token clients send to Meet admin/meeting APIs. */
export function getInternalApiKey(): string {
  return firstNonEmpty(process.env.INTERNAL_API_KEY, process.env.MEET_BEARER_TOKEN);
}

/** Secret for signing outbound requests to Socket (x-webhook-secret / HMAC). */
export function getSocketWebhookSigningSecret(): string {
  return firstNonEmpty(process.env.SOCKET_WEBHOOK_SIGNING_SECRET, process.env.WEBHOOK_SECRET);
}

/** Verifies Odoo identity sync on `/api/internal/odoo-user/sync`. */
export function getOdooWebhookSigningSecret(): string {
  return firstNonEmpty(
    process.env.ODOO_WEBHOOK_SIGNING_SECRET,
    process.env.MEET_WEBHOOK_SECRET,
    process.env.WEBHOOK_SECRET
  );
}

/** Meet → Socket webhook target (`…/api/webhook`). */
export function getSocketWebhookUrl(): string {
  return trim(process.env.SOCKET_WEBHOOK_URL);
}

/** Socket REST API base (`…/api/v1`), derived from webhook URL when unset. */
export function getSocketApiV1BaseUrl(): string | null {
  const webhookUrl = getSocketWebhookUrl();
  if (webhookUrl) {
    return stripTrailingSlashes(webhookUrl.replace(/\/webhook\/?$/i, ""));
  }
  const explicit = trim(process.env.SOCKET_API_BASE_URL);
  if (explicit) {
    return stripTrailingSlashes(explicit);
  }
  return null;
}

export function getSocketPublicEndpoint(): string {
  return trim(process.env.NEXT_PUBLIC_SOCKET_ENDPOINT);
}

/** Origin of the Next.js app for chat embed iframes. */
export function getNextjsEmbedOrigin(): string {
  return firstNonEmpty(process.env.NEXT_PUBLIC_NEXTJS_URL, process.env.NEXT_PUBLIC_APP_URL, process.env.FREIBURGER_URL);
}

export function getMeetPublicBaseUrl(): string {
  return firstNonEmpty(process.env.BASE_URL, "http://localhost:4000");
}

export function getDatabaseUrl(): string {
  return trim(process.env.DATABASE_URL);
}

export function getFreiburgerUrl(): string {
  return trim(process.env.FREIBURGER_URL);
}

export function getTimerSeconds(): number {
  const n = Number(process.env.TIMER_SECONDS);
  return Number.isFinite(n) && n > 0 ? n : 5;
}

export function getLivekitConfig(): {
  url: string;
  apiKey: string;
  apiSecret: string;
} {
  return {
    url: trim(process.env.LIVEKIT_URL),
    apiKey: trim(process.env.LIVEKIT_API_KEY),
    apiSecret: trim(process.env.LIVEKIT_API_SECRET)
  };
}

export function getJwtConfig(): { secret: string; expiresIn: string } {
  return {
    secret: firstNonEmpty(process.env.JWT_SECRET, "mySuperSecretKey123@!!"),
    expiresIn: firstNonEmpty(process.env.JWT_EXPIRES_IN, "1h")
  };
}

export function getSmtpConfig(): {
  host: string;
  port: number;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
  isEnabled: boolean;
} {
  const port = parseInt(process.env.SMTP_PORT || "587", 10);
  return {
    host: trim(process.env.SMTP_HOST),
    port: Number.isFinite(port) ? port : 587,
    user: trim(process.env.SMTP_USER),
    password: trim(process.env.SMTP_PASSWORD),
    fromEmail: firstNonEmpty(process.env.SMTP_FROM_EMAIL, "noreply@freiburger-meet.com"),
    fromName: firstNonEmpty(process.env.SMTP_FROM_NAME, "Freiburger Meet"),
    isEnabled: process.env.IS_EMAIL_ENABLED === "true"
  };
}
